import type { PostHogConfig } from 'posthog-js';

type BeforeSendHook = Exclude<NonNullable<PostHogConfig['before_send']>, readonly unknown[]>;

/** A developer's dev server: `localhost`, `127.0.0.1` or `::1`. */
export function isLocalDevHost(hostname: string): boolean {
  const host = hostname
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, '');
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

/** `$exception` plus the typed error event each app pairs with it (`*.error_occurred`). */
const ERROR_EVENT = /^\$exception$|(^|\.)error_occurred$/;

const dropErrors: BeforeSendHook = (event) =>
  event && ERROR_EVENT.test(event.event) ? null : event;

/**
 * Errors from a local dev server aren't product signal, and PostHog can't fetch
 * a dev server's files to symbolicate them, so they only inflate error tracking's
 * unresolved-frames figure. On a local host this turns off exception autocapture
 * and drops `$exception` and the typed `*.error_occurred` event before send
 * (which also catches manual `captureException` calls). The typed event goes
 * too: `environment` doesn't reliably mark a local build (mission-control-web
 * reports 'live'), so local errors would mix into the error dashboards. Other
 * product analytics are unchanged.
 *
 * Loopback only: every local error in PostHog has come from `localhost`, none
 * from LAN IPs or `.local` hostnames. Widen `isLocalDevHost` if that changes.
 */
export function withoutLocalDevErrors(
  options: Partial<PostHogConfig>,
  hostname: string,
): Partial<PostHogConfig> {
  if (!isLocalDevHost(hostname)) return options;
  const existing = options.before_send;
  const chain = Array.isArray(existing) ? existing : existing ? [existing] : [];
  return { ...options, capture_exceptions: false, before_send: [dropErrors, ...chain] };
}
